import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cliente } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { formatearCRC } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export function IntegracionBcObraPantalla() {
  const qc = useQueryClient();
  const { obra = '' } = useParams();
  const [params] = useSearchParams();
  const fecha = params.get('fecha') ?? '';

  const previewQ = useQuery({
    queryKey: ['bc-preview', obra],
    queryFn: () => cliente.bc.preview(obra),
    enabled: !!obra,
  });
  const preview = previewQ.data?.data ?? null;

  const reportarM = useMutation({
    mutationFn: () => cliente.bc.reportar(obra),
    onSuccess: (res) => {
      const r = res.data?.resultados ?? [];
      const ok = r.filter((x) => x.ok).length;
      const fail = r.length - ok;
      toast.success(`Reportadas ${ok} partida(s) en BC${fail ? `, ${fail} con error` : ''}`);
      qc.invalidateQueries({ queryKey: ['bc-preview', obra] });
      qc.invalidateQueries({ queryKey: ['bc-resumen'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Error al reportar'),
  });

  const registrarM = useMutation({
    mutationFn: () => cliente.bc.registrar(obra, fecha),
    onSuccess: () => {
      toast.success(`Obra ${obra} registrada (posteada) en BC`);
      qc.invalidateQueries({ queryKey: ['bc-preview', obra] });
      qc.invalidateQueries({ queryKey: ['bc-resumen'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Error al registrar'),
  });

  const inicializarM = useMutation({
    mutationFn: () => cliente.bc.inicializar(obra),
    onSuccess: () => {
      toast.success(`Producción de ${obra} inicializada en BC`);
      qc.invalidateQueries({ queryKey: ['bc-preview', obra] });
      qc.invalidateQueries({ queryKey: ['bc-resumen'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Error al inicializar'),
  });

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/integracion-bc">
            <ArrowLeft className="size-4" /> Obras
          </Link>
        </Button>
        <div>
          <h1 className="font-semibold text-xl">Detalle BC · {obra}</h1>
          <p className="text-muted-foreground text-sm">
            Avance de ObrasControl vs Business Central por partida.
          </p>
        </div>
      </div>

      {previewQ.isLoading && <p className="text-muted-foreground text-sm">Cargando…</p>}

      {preview && (
        <>
          <Card className="flex flex-wrap items-center justify-between gap-4 p-4">
            <div className="text-sm">
              {!preview.produccion_inicializada ? (
                <span className="font-medium text-rose-700">
                  Producción no inicializada en BC
                  {!preview.inicializar_disponible &&
                    ' — primero "Importar avance de Excel" (o usar el botón cuando esté disponible)'}
                </span>
              ) : preview.ya_registrado ? (
                <span className="font-medium text-emerald-700">
                  Al día — nada nuevo que reportar
                </span>
              ) : (
                <>
                  <span className="font-semibold">{preview.n_cambios}</span> partida(s) a reportar ·
                  monto a registrar:{' '}
                  <span className="font-semibold">{formatearCRC(preview.total_a_registrar)}</span>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {!preview.produccion_inicializada && (
                <Button
                  variant="secondary"
                  onClick={() => inicializarM.mutate()}
                  disabled={inicializarM.isPending || !preview.inicializar_disponible}
                  title={
                    preview.inicializar_disponible
                      ? 'Trae el presupuesto a Producción en BC'
                      : 'Pendiente: el partner de BC debe publicar el web service de Inicializar'
                  }
                >
                  {inicializarM.isPending ? 'Inicializando…' : 'Inicializar producción'}
                </Button>
              )}
              <Button
                onClick={() => reportarM.mutate()}
                disabled={
                  reportarM.isPending || preview.ya_registrado || !preview.produccion_inicializada
                }
              >
                {reportarM.isPending ? 'Reportando…' : `Reportar (${preview.n_cambios})`}
              </Button>
              <Button
                variant="secondary"
                onClick={() => registrarM.mutate()}
                disabled={registrarM.isPending || !preview.registrar_disponible}
                title={
                  preview.registrar_disponible
                    ? 'Postea la producción en BC'
                    : 'Pendiente: el partner de BC debe publicar el web service de Registrar'
                }
              >
                {registrarM.isPending ? 'Registrando…' : 'Registrar'}
              </Button>
            </div>
          </Card>

          <Card className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partida</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">OC %</TableHead>
                  <TableHead className="text-right">BC %</TableHead>
                  <TableHead className="text-right">Δ</TableHead>
                  <TableHead className="text-right">Monto a registrar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.partidas.map((p) => (
                  <TableRow key={p.partida} className={cn(p.se_reportaria && 'bg-amber-50')}>
                    <TableCell className="font-mono text-xs">{p.partida}</TableCell>
                    <TableCell className="text-xs">{p.nombre}</TableCell>
                    <TableCell className="text-right">
                      {p.oc_pct == null ? '—' : p.oc_pct.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right">{p.bc_pct.toFixed(1)}</TableCell>
                    <TableCell
                      className={cn(
                        'text-right',
                        p.se_reportaria && 'font-semibold text-amber-700',
                      )}
                    >
                      {p.delta == null ? '—' : `${p.delta >= 0 ? '+' : ''}${p.delta.toFixed(1)}`}
                    </TableCell>
                    <TableCell className="text-right">
                      {p.monto_a_registrar > 0 ? formatearCRC(p.monto_a_registrar) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {!preview.registrar_disponible && (
            <p className="text-muted-foreground text-xs">
              "Registrar" se habilita cuando el partner de BC publique el web service (ver
              docs/integracion-bc-registrar.md). Por ahora podés Reportar y registrar a mano en BC.
            </p>
          )}
        </>
      )}
    </div>
  );
}
